import { UserIdLookup } from 'iam/UserIdLookup'
import { configure } from 'mobx'
import { PayoutsAdminRootViewModel } from 'payouts/admin/PayoutsAdminRootViewModel'
import { createBigMoneyPayoutsAdminAPI } from 'payouts/admin/API'
import { merge, Observable } from 'rxjs'
import { tap } from 'rxjs/operators'

/* Stores */
import AuthStore from 'auth/AuthStore'
import BillingStore from 'billing/BillingStore'
import { PayoutReviewStore } from 'payouts/admin/PayoutReviewStore'
import { WorkRealtime } from 'work-realtime/WorkRealtime'
import ConversationStore from './messaging/ConversationStore'
import CouponStore from 'billing/CouponStore'
import DesktopNotificationStore from 'notifications/DesktopNotificationStore'
import DocumentStore from 'documents/DocumentStore'
import DocumentAccessStore from 'documents/DocumentAccessStore'
import DocumentTagStore from 'documents/DocumentTagStore'
import DocumentTypeStore from 'documents/DocumentTypeStore'
import DocumentVersionStore from 'documents/DocumentVersionStore'
import InvitationStore from './iam/InvitationStore'
import JobStore from 'jobs/JobStore'
import { MessageBankStore } from 'messaging/message-bank/MessageBankStore'
import MemberStore from 'iam/MemberStore'
import MilestoneStore from 'jobs/MilestoneStore'
import NotificationPreferencesStore from 'notifications/NotificationPreferencesStore'
import NotificationStore from 'notifications/NotificationStore'
import OpportunityStore, { PickupResult } from 'opportunities/OpportunityStore'
import ProviderStore from 'jobs/ProviderStore'
import QuestionStore from 'jobs/QuestionStore'
import SummaryStore from 'jobs/SummaryStore'
import PermissionStore from 'iam/PermissionStore'
import PublishedLegendStore from 'legends/PublishedLegendStore'
import RoleStore from 'iam/RoleStore'
import SessionStore, { GetCurrentMember } from 'iam/SessionStore'
import SkillStore from 'iam/SkillStore'
import TeamMemberStore from 'iam/TeamMemberStore'
import TeamRoleStore from 'iam/TeamRoleStore'
import TeamStore from 'iam/TeamStore'
import UserStore from 'iam/UserStore'
import WorkspaceMembershipStore from 'iam/WorkspaceMembershipStore'
import WorkspaceStore from 'iam/WorkspaceStore'
import { CustomerStore } from 'iam/CustomerStore'

/* APIs */
import { createBillingAPI } from 'billing/API'
import { createDocumentAPI } from 'documents/API'
import { createIAMAPI } from 'iam/API'
import { createLegendAPI } from 'legends/API'
import { createMessageBankAPI, createMessagingAPI } from 'messaging/API'
import { createNotificationsAPI } from 'notifications/API'
import { createOpportunitiesAPI } from 'opportunities/API'
import {
  createBookkeepingAPI,
  createIntegrationsAPI,
  createWorkAPI,
} from 'jobs/API'
import { createWorkspacePostingAPI } from 'admin/staffing/API'

/* Controllers */
import AlertsController from 'alerts/AlertsController'
import ContextController from './ContextController'

/* Adapters */
import CardPaymentMethodEditorAdapter from 'billing/payment-methods/CardPaymentMethodEditorAdapter'
import { DocumentActionsAdapter } from 'documents/DocumentActionsAdapter'
import { DocumentDetailsAdapter } from 'documents/DocumentDetailsAdapter'

import JobCreationAdapter from 'jobs/JobCreationAdapter'
import { JobDocumentsAdapter } from 'jobs/JobDocumentsViewModelAdapter'
import OpportunitiesAdapter from 'opportunities/OpportunitiesAdapter'
import PaymentMethodSelectorAdapter from 'billing/payment-methods/PaymentMethodSelectorAdapter'
import { JoinWorkspaceAsAdminAdapter } from 'user-management/views/JoinWorkspaceAsAdminDialog/JoinWorkspaceAsAdminAdapter'

/* View Models */
import AuthenticationViewModel from 'iam/views/AuthenticationContainer/AuthenticationContainerState'
import ConversationViewModel from 'messaging/components/ConversationView/ConversationViewModel'
import { DocumentDetailsRootViewModel } from 'documents/views/DocumentRoot/DocumentDetailsRootViewModel'
import JobCreationRootViewModel from './jobs/creation/JobCreationRootViewModel'
import JobRootViewModel from 'jobs/views/JobRoot/JobRootViewModel'
import NotificationsSidebarViewModel from 'notifications/views/NotificationsSidebar/NotificationsSidebarViewModel'
import MessageBankDialogViewModel from 'messaging/message-bank/MessageBankDialog/MessageBankDialogViewModel'
import PaymentMethodsRootViewModel from 'billing/payment-methods/PaymentMethodsRootViewModel'
import StripeCallbackViewModel from 'billing/screens/StripeCallback/StripeCallbackViewModel'
import { ProPayoutsViewModel } from 'payouts/pro/ProPayoutsViewModel'

/* Misc */
import { Env } from 'infra/env'
import { KeyPress } from 'utils/keyboard-util'
import { LastSeenSync } from 'iam/LastSeenSync'
import { localStorage } from 'infra/storage'
import { MessageSystemNotificationsFlow } from 'messaging/MessageNotifications'
import { MessageSounds } from 'messaging/MessageSounds'
import { NotificationsToSystemNotifications } from 'notifications/NotificationsToSystemNotifications'
import { Shortcuts } from 'shortcuts/Shortcuts'
import SoundPlayer from 'sound/SoundPlayer'
import User from 'iam/models/User'
import { Id, PageActivity, Router } from 'types/definitions'
import { FilterStorage } from 'infra/FiltersViewModel'
import { LastWorkspaceStorage } from 'infra/LastWorkspace'
import SureprepStore from 'sureprep/SureprepStore'
import { UserDTO } from 'iam/dtos/user'
import JobManagementViewModel, {
  JobManagementViewModelFactory,
} from 'jobs/views/JobManagement/JobManagementViewModel'
import JobSearchAdapter from 'jobs/infra/JobSearchAdapter'
import JobSearchStore from 'jobs/views/JobManagement/JobSearchStore'
import UserDataStore from 'iam/UserDataStore'
import { createFeatureFlagsAPI } from 'featureflags/API'
import FeatureFlagStore from 'featureflags/FeatureFlagStore'
import { createJobStatsAPI } from 'jobs/job-stats/API'
import JobStatsStore from 'jobs/job-stats/JobStatsStore'
import { PayoutsAdminModuleAdapter } from 'payouts/admin/PayoutsAdminModuleAdapter'
import AdminViewModel from 'admin/AdminViewModel'
import InventoryStore from 'admin/InventoryStore'
import ScopeChangeRequestStore from 'admin/ScopeChangeRequestStore'
import { createInventoryAPI } from 'admin/API'
import CompleteRegistrationViewModel from 'iam/views/CompleteRegistration/CompleteRegistrationViewModel'
import { AdminPayoutHistoryStore } from './payouts/admin/AdminPayoutHistoryStore'
import PayoutMethodStore from './payouts/pro/PayoutMethodStore'
import { createBigMoneyPayoutsAPI } from 'payouts/pro/API'
import { createStreamDownloader } from './infra/StreamDownloader'
import JobProgressionStore from 'jobs/progression/JobProgressionStore'
import { createJobProgressionAPI } from 'jobs/progression/API'
import TermsModalViewModel from 'iam/views/TermsModal/TermsModalViewModel'
import ProSoftwareReviewModalViewModel from 'iam/views/ProSoftwareReviewModal/ProSoftwareReviewModalViewModel'
import TermsOfUseStore from 'iam/TermsOfUseStore'
import { JobTicketStore } from './jobs/tickets/JobTicketStore'
import { createScopeChangeRequestAPI } from 'admin/scope-change-requests/API'
import MultiFactorAuthStore from './auth/MultiFactorAuthStore'
import { ProPayoutsModuleAdapter } from './payouts/pro/ProPayoutsModuleAdapter'
import { ProPayoutStore } from './payouts/pro/ProPayoutStore'
import { SupportingItemsAdapter } from './admin/SupportingItemAdapter'
import { InsightStore } from './jobs/insights/InsightStore'
import InfoGatheringStore from './jobs/InfoGatheringStore'
import FirmDashboardViewModel from './jobs/screens/FirmDashboard/FirmDashboardViewModel'
import ConsultationStore from 'jobs/consultations/ConsultationStore'
import { BundleStore } from 'admin/bundle/BundleStore'
import { createBundleAPI } from 'admin/bundle/API'
import { OpportunityOfferStore } from './opportunities/offers/OpportunityOfferStore'
import ConsultationModalViewModel from './opportunities/views/ConsultationModal/ConsultationModal'
import JobCreationDialogViewModel from 'jobs/creation/views/JobCreationDialog/JobCreationDialogViewModel'
import OCRScopingJobsViewModel from 'jobs/screens/OCRScopingJobs/OCRScopingJobsViewModel'
import OCRScopeWorkStore from 'wunder/OCRScopeWorkStore'
import { createWunderAPI } from 'wunder/API'
import IAMProviderStore from 'iam/ProviderStore'
import WorkspacePostingStore from 'admin/WorkspacePostingStore'
import WorkspacePostingOpportunityStore from './opportunities/workspace-postings-opportunities/WorkspacePostingOpportunityStore'
import { createWorkspacePostingOpportunitiesAPI } from './opportunities/workspace-postings-opportunities/API'
import WorkspaceOpportunitiesAdapter from './opportunities/workspace-postings-opportunities/WorkspaceOpportunitiesAdapter'
import { CallCenterViewModel } from 'call-center/CallCenterViewModel'
import OpportunitiesRootViewModel from './opportunities/OpportunitiesRootViewModel'
import WorkspaceOpportunityInvitationStore from './opportunities/invitations/WorkspaceOpportunityInvitationStore'
import { createJobRatingAPI } from './jobs/ratings/API'
import JobRatingStore from 'jobs/ratings/JobRatingStore'
import { UserManagementViewModel } from './user-management/views/UserManagement/UserManagementViewModel'
import { UserDetailsViewModel } from 'user-management/views/UserDetails/UserDetailsViewModel'
import SearchableUserStore from './iam/SearchableUserStore'
import { createBookkeepingManagerAPI } from 'admin/bookkeeping/API'
import BookkeepingManagerStore from 'admin/BookkeepingManagerStore'
import BookkeepingProgressionStore from 'jobs/bookkeeping/BookkeepingProgressionStore'
import CheckoutViewModel from 'jobs/views/JobRoot/Checkout/CheckoutViewModel'
import { createFreshbooksAPI } from 'freshbooks/API'
import DocumentRequestStore from 'jobs/document-requests/DocumentRequestStore'
import { createRoomsApi } from './messaging/rooms/API'
import RoomStore from './messaging/rooms/RoomStore'
import BusinessProfileStore from 'jobs/info-gathering/business-info/BusinessProfileStore'
import { createBusinessProfilesAPI } from 'jobs/info-gathering/business-info/API'
import IndividualProfileStore from 'jobs/info-gathering/individual-info/IndividualProfileStore'
import { createIndividualProfileAPI } from 'jobs/info-gathering/individual-info/API'
import TaxRefundMethodStore from 'jobs/info-gathering/tax-refund-method/TaxRefundMethodStore'
import { createTaxRefundMethodsAPI } from 'jobs/info-gathering/tax-refund-method/API'
import { ClientUserManagementViewModel } from 'client-user-management/views/ClientUserManagement/ClientUserManagementViewModel'
import { ClientUserDetailsViewModel } from 'client-user-management/views/ClientUserDetails/ClientUserDetailsViewModel'
import AvailableJobProgressionFlowsStore from 'jobs/progression/AvailableJobProgressionFlows/AvailableJobProgressionFlowsStore'
import AdminPayoutMethodStore from './payouts/admin/AdminPayoutMethodStore'
import { DocumentManualVerificationViewModel } from 'jobs/views/JobRoot/Job/DocumentManualVerificationModal/DocumentManualVerificationViewModel'
import TxfJobStore from 'jobs/TxfJobStore'
import { SessionRecordingFeature } from 'tracking/services'
import { NativePlatformDelegate } from 'shell/platform/NativePlatformDelegate'
import { DefaultPlatformDelegate } from 'shell/platform/DefaultPlatformDelegate'

configure({ enforceActions: 'observed' })

/**
 * The Root Store links the stores and models together.
 */
export class RootStore {
  /**
   * Router proxy.
   */
  private router: Router = createProxyRouter(this)

  // Gets replaced by the application.
  routerInstance = createUnsetRouterInstance()
  private readonly platformDelegate = NativePlatformDelegate.isSupported
    ? new NativePlatformDelegate()
    : new DefaultPlatformDelegate()

  authStore = new AuthStore(
    this.router,
    this.pageActivity$,
    () => this.iamAPI.users.logout(),
    () => this.sessionStore.workspace.config.signup?.logout_url,
    () => this.sessionStore.isFederatedLogout,
    () => this.platformDelegate.getAuthTokenDelegate().getToken(),
    (token: string) => this.iamAPI.auth.authenticate(token),
    (user: UserDTO) => this.userStore.users.set(user),
    (token: string, publicId: string) =>
      this.iamAPI.auth.fetchUser(token, publicId),
  )

  private readonly getToken = () => this.authStore.getToken()
  private readonly getCurrentWorkspace = () => this.sessionStore.workspace
  private readonly getCurrentUser = () => this.sessionStore.user
  private readonly getCurrentMember: GetCurrentMember = (workspaceId?: Id) =>
    this.sessionStore.getCurrentMember(workspaceId)

  private readonly soundPlayer = new SoundPlayer(localStorage)
  private readonly newAlert = () => this.alertsController.newAlert()
  private readonly token$ = this.authStore.token$

  private readonly downloader = createStreamDownloader()

  searchServiceItems = (
    workspaceIds: Id[],
    limit: number,
    searchText?: string,
    cursor?: string,
  ) =>
    this.inventoryStore.fetchInventoryItems(
      workspaceIds,
      limit,
      searchText,
      cursor,
    )

  private readonly makeJobManagementViewModel: JobManagementViewModelFactory = (
    storageIdentifier: string,
  ) =>
    new JobManagementViewModel(
      new JobSearchAdapter(this.jobSearchStore),
      this.newAlert,
      (params) => this.publishedLegendStore.findLegends(params),
      this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships,
      this.teamMemberStore.fetchUserTeamMembers,
      this.sessionStore.currentMember$,
      this.getCurrentMember,
      this.jobTicketStore,
      (ns) => new FilterStorage(localStorage, ns),
      localStorage,
      this.userDataStore,
      this.teamMemberStore.filterTeamMembers.bind(this.teamMemberStore),
      () => this.jobRootViewModel.canEditDraftJobAsAdmin,
      (params) => this.teamStore.getUserTeams(params),
      () => this.jobRootViewModel.useEditDraftJobForFirmAdmin,
      this.opportunitiesRootViewModel.opportunitiesViewModel.offersVM.handleOfferResponse,
      this.jobStore,
      storageIdentifier,
      this.searchServiceItems,
      this.authenticationViewModel.workspaceSelectorState,
      this.providerStore,
      this.teamMemberStore.search,
      this.memberStore.search,
      this.jobCreationDialogViewModel,
      this.consultationStore,
      () => this.documentTypeStore.fetchTypes(),
    )

  /* APIs */
  workRealtime$ = WorkRealtime({
    baseURL: Env.WORK_DOTNET_API,
    token$: this.token$,
  })

  iamAPI = createIAMAPI({
    baseURL: Env.IAM_API,
    token$: this.token$,
  })

  billingAPI = createBillingAPI({
    baseURL: Env.BILLING_API,
    token$: this.token$,
  })

  documentAPI = createDocumentAPI({
    baseURL: Env.DOCUMENT_API,
    token$: this.token$,
  })

  legendAPI = createLegendAPI({
    baseURL: Env.LEGEND_API,
    token$: this.token$,
  })

  opsAPI = createOpportunitiesAPI(
    {
      baseURL: Env.WORK_DOTNET_API,
      token$: this.token$,
    },
    this.workRealtime$,
  )

  workAPI = createWorkAPI(
    {
      baseURL: Env.WORK_DOTNET_API,
      token$: this.token$,
    },
    this.workRealtime$,
  )

  individualProfileAPI = createIndividualProfileAPI({
    baseURL: Env.WORK_DOTNET_API,
    token$: this.token$,
  })

  businessProfileAPI = createBusinessProfilesAPI({
    baseURL: Env.WORK_DOTNET_API,
    token$: this.token$,
  })

  taxRefundMethodAPI = createTaxRefundMethodsAPI({
    baseURL: Env.WORK_DOTNET_API,
    token$: this.token$,
  })

  jobStatsAPI = createJobStatsAPI({
    baseURL: Env.WORK_DOTNET_API,
    token$: this.token$,
  })

  integrationAPI = createIntegrationsAPI({
    baseURL: Env.WORK_DOTNET_API,
    token$: this.token$,
  })

  messagingAPI = createMessagingAPI({
    baseURL: Env.MESSAGE_API,
    token$: this.token$,
  })

  roomsAPI = createRoomsApi({
    baseURL: Env.MESSAGE_API,
    token$: this.token$,
  })

  notificationAPI = createNotificationsAPI({
    baseURL: Env.NOTIFICATION_API,
    token$: this.token$,
  })

  messageBankAPI = createMessageBankAPI({
    baseURL: Env.MESSAGE_API,
    token$: this.token$,
  })

  featureFlagsAPI = createFeatureFlagsAPI({
    baseURL: Env.WORK_DOTNET_API,
    token$: this.token$,
  })

  inventoryAPI = createInventoryAPI({
    baseURL: Env.WORK_DOTNET_API,
    token$: this.token$,
  })

  bundleAPI = createBundleAPI({
    baseURL: Env.WORK_DOTNET_API,
    token$: this.token$,
  })

  scopeChangeRequestAPI = createScopeChangeRequestAPI({
    baseURL: Env.WORK_DOTNET_API,
    token$: this.token$,
  })

  bookkeepingManagerAPI = createBookkeepingManagerAPI(
    {
      baseURL: Env.WORK_DOTNET_API,
      token$: this.token$,
    },
    this.workRealtime$,
  )

  jobProgressionAPI = createJobProgressionAPI(
    {
      baseURL: Env.WORK_DOTNET_API,
      token$: this.token$,
    },
    this.workRealtime$,
  )

  bookkeepingApi = createBookkeepingAPI(
    {
      baseURL: Env.WORK_DOTNET_API,
      token$: this.token$,
    },
    this.workRealtime$,
  )

  bigMoneyPayoutsAdminAPI = isPayoutsEnabled()
    ? createBigMoneyPayoutsAdminAPI({
        // NOTE: If the env var is not set, then no feature that
        // calls the API will execute.
        baseURL: Env.BIGMONEY_API ?? '/',
        token$: this.token$,
      })
    : null

  bigMoneyPayoutsAPI = isPayoutsEnabled()
    ? createBigMoneyPayoutsAPI({
        // NOTE: If the env var is not set, then no feature that
        // calls the API will execute.
        baseURL: Env.BIGMONEY_API ?? '/',
        token$: this.token$,
      })
    : null

  wunderAPI = createWunderAPI({
    baseURL: Env.WUNDER_API,
    token$: this.token$,
  })

  freshbooksAPI = createFreshbooksAPI({
    baseURL: Env.FRESHBOOKS_API_EXTERNAL,
    token$: this.token$,
  })

  jobRatingAPI = createJobRatingAPI({
    baseURL: Env.WORK_DOTNET_API,
    token$: this.token$,
  })

  workspacePostingAPI = createWorkspacePostingAPI({
    baseURL: Env.IAM_API,
    token$: this.token$,
  })

  workspacePostingOpsAPI = createWorkspacePostingOpportunitiesAPI({
    baseURL: Env.IAM_API,
    token$: this.token$,
  })

  sureprepStore: SureprepStore = new SureprepStore(this.integrationAPI.sureprep)

  userIdLookup = UserIdLookup(this.iamAPI.users)

  /**
   * Consultation Modal View Model.
   */
  consultationModalViewModel = new ConsultationModalViewModel()

  /* Stores */
  userStore: UserStore = new UserStore(
    this.iamAPI,
    (user) => this.conversationStore.getParticipantStatusStreamForUser(user),
    (user) => this.conversationStore.fetchOnlineStatusForUser(user),
    this.authStore.session$,
  )

  workspaceStore = new WorkspaceStore(this.iamAPI)

  customerStore = new CustomerStore(this.iamAPI.customers)

  memberStore = new MemberStore(
    this.iamAPI,
    this.workspaceStore.primeWorkspace,
    (props) => this.userStore.primeUser(props) ?? User.none,
    (props) => this.roleStore.roles.set(props),
    (props) => this.skillStore.skills.set(props),
  )

  searchableUserStore = new SearchableUserStore(
    this.iamAPI,
    (props) => this.memberStore.primeFullMember(props),
    (props) => this.workspaceStore.primeFullWorkspace(props),
    (userId) =>
      this.conversationStore.getParticipantStatusStreamForSearchableUser(
        userId,
      ),
    (userId) =>
      this.conversationStore.fetchOnlineStatusForSearchableUser(userId),
  )

  sessionStore: SessionStore = new SessionStore(
    () => this.userStore.currentUser,
    this.workspaceStore,
    this.memberStore,
  )

  multiFactorAuthStore = new MultiFactorAuthStore(
    this.iamAPI,
    this.getCurrentUser,
  )

  permissionStore = new PermissionStore(
    this.iamAPI,
    this.sessionStore.currentMember$,
  )

  workspaceMembershipStore = new WorkspaceMembershipStore(
    this.workspaceStore,
    this.memberStore,
    this.iamAPI,
    this.getCurrentUser,
  )

  teamStore: TeamStore = new TeamStore(
    this.iamAPI,
    this.workspaceStore.primeWorkspace,
    (props) => this.teamMemberStore.teamMembers.set(props),
    this.sessionStore.currentMember$,
  )

  teamRoleStore = new TeamRoleStore(
    this.iamAPI,
    this.getCurrentMember,
    this.sessionStore.currentMember$,
  )

  teamMemberStore = new TeamMemberStore(
    this.iamAPI,
    this.getCurrentMember,
    this.memberStore.getMember,
    this.memberStore.primeMember,
    this.teamStore.teams.set,
    this.teamRoleStore.teamRoles.set,
    this.sessionStore.currentMember$,
  )

  conversationStore: ConversationStore = new ConversationStore(
    this.messagingAPI,
    this.workspaceStore.primeWorkspace,
    this.memberStore.primeMemberByPublicId,
    this.userStore.users.set,
    (props) => this.progressionStore.prime(props),
    this.getCurrentUser,
    this.sessionStore.currentUser$,
    this.pageActivity$,
  )

  roomStore = new RoomStore(
    this.messagingAPI,
    this.roomsAPI,
    this.memberStore.primeMemberByPublicId,
    this.conversationStore.primeConversation,
    this.conversationStore.fetchConversation,
  )

  messageBankStore = new MessageBankStore(
    this.messageBankAPI,
    this.workspaceStore.primeWorkspace,
  )

  publishedLegendStore = new PublishedLegendStore(
    this.legendAPI,
    this.workspaceStore.primeWorkspace,
  )

  jobStore = new JobStore(
    this.workAPI,
    this.getCurrentMember,
    this.teamMemberStore.getTeamMember,
    this.teamMemberStore.getTeamMembers,
    (props) => this.conversationStore.primeConversation(props),
    this.workspaceStore.primeWorkspace,
    (props) => this.userStore.primeUser(props) ?? User.none,
    this.memberStore.primeMember,
    this.teamStore.primeTeam,
    this.publishedLegendStore.primePublishedLegend,
    this.conversationStore.newMessages$,
    (ids) => this.billingStore.fetchBillingTransactions(ids),
    this.workRealtime$,
    this.memberStore.primeMemberByPublicId,
    this.roomStore.primeRoom,
  )

  jobStatsStore = new JobStatsStore(this.jobStatsAPI)

  jobSearchStore = new JobSearchStore(
    this.workAPI,
    this.workspaceStore.primeWorkspace,
    this.conversationStore.primeConversation,
    this.publishedLegendStore.primePublishedLegend,
    this.teamStore.primeTeam,
    () => this.userStore.currentUser?.publicId ?? null,
    this.teamMemberStore.getTeamMember,
    this.getCurrentMember,
    this.workRealtime$,
    this.jobStore.localJobChanges$,
  )

  opportunityStore = new OpportunityStore(
    this.opsAPI,
    (props) => this.userStore.primeUser(props) ?? User.none,
    this.workspaceStore.primeWorkspace,
    this.getCurrentMember,
    this.sessionStore.currentMember$,
    // trust me
    (...args) =>
      this.jobStore.pickupInternal(...args) as any as Promise<PickupResult>,
    this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships,
  )

  summaryStore = new SummaryStore(this.workAPI)
  milestoneStore = new MilestoneStore(this.workAPI, this.jobStore.primeJob)
  questionStore = new QuestionStore(this.workAPI)
  providerStore = new ProviderStore(
    this.workAPI,
    this.memberStore.primeMemberByPublicId,
    this.memberStore.getManyByPublicIds,
    this.opportunityStore,
  )

  documentStore: DocumentStore = new DocumentStore(
    this.documentAPI.documents,
    this.memberStore.primeMember,
    this.getCurrentMember,
    this.getToken,
    (props) => this.documentTagStore.tags.set(props),
    (props) => this.documentAccessStore.accesses.set(props),
    (props) => this.documentVersionStore.versions.set(props),
    (props) => this.documentTypeStore.types.set(props),
    (id) => this.documentTagStore.tags.get(id),
    (id) => this.documentAccessStore.forDocument(id),
    (id) => this.documentVersionStore.forDocument(id),
    this.workspaceStore.primeWorkspace,
    this.platformDelegate.getDocumentDownloadDelegate,
    this.sessionStore.currentMember$,
  )

  documentVersionStore = new DocumentVersionStore(
    this.documentAPI,
    this.memberStore.getMember,
    this.memberStore.primeMember,
    this.documentStore.documents.get,
  )

  documentAccessStore = new DocumentAccessStore(
    this.documentAPI,
    this.memberStore.primeMember,
    this.memberStore.getMember,
    this.jobStore.getJob,
    (id) => this.documentStore.documents.get(id),
    (id) =>
      this.documentStore.fetchDocument(id, {
        history: true,
        tags: true,
      }),
  )

  documentTagStore = new DocumentTagStore(
    this.documentAPI,
    this.getCurrentMember,
  )

  documentTypeStore = new DocumentTypeStore(this.documentAPI)

  inventoryStore = new InventoryStore(
    this.inventoryAPI,
    this.workspaceStore,
    this.workspaceStore.primeWorkspace,
    (props) => this.userStore.primeUser(props) ?? User.none,
    this.documentTypeStore.primeDocumentTypeByShortId,
  )

  bundleStore = new BundleStore(
    this.bundleAPI,
    (props) => this.userStore.primeUser(props) ?? User.none,
    this.workspaceStore.primeWorkspace,
    this.workspaceStore,
  )

  scopeChangeRequestStore = new ScopeChangeRequestStore(
    this.scopeChangeRequestAPI,
    this.workspaceStore.primeWorkspace,
    (props) => this.userStore.primeUser(props) ?? User.none,
    this.jobStore.primeJob,
  )

  iamProviderStore = new IAMProviderStore(this.iamAPI.providers)

  workspacePostingStore = new WorkspacePostingStore(
    this.workspacePostingAPI,
    this.workspaceStore.primeWorkspace,
    (props) => this.userStore.primeUser(props) ?? User.none,
    this.iamProviderStore.primeProvider,
  )

  bookkeepingManagerStore = new BookkeepingManagerStore(
    this.bookkeepingManagerAPI,
    this.memberStore.primeMemberByPublicId,
  )

  payoutReviewStore = this.bigMoneyPayoutsAdminAPI
    ? new PayoutReviewStore(
        this.bigMoneyPayoutsAdminAPI,
        (props) => this.userStore.primeUser(props) ?? User.none,
        this.workspaceStore.primeWorkspace,
        this.getCurrentUser,
      )
    : null

  adminPayoutHistoryStore = this.bigMoneyPayoutsAdminAPI
    ? new AdminPayoutHistoryStore(
        this.bigMoneyPayoutsAdminAPI,
        (props) => this.userStore.primeUser(props) ?? User.none,
        this.workspaceStore.primeWorkspace,
        this.getCurrentUser,
      )
    : null

  proPayoutStore = this.bigMoneyPayoutsAPI
    ? new ProPayoutStore(
        this.bigMoneyPayoutsAPI,
        this.workspaceStore.primeWorkspace,
        this.jobStore.primeJob,
        this.jobStore.fetchJob,
      )
    : null

  jobTicketStore = new JobTicketStore(this.workAPI)

  jobInsightStore = new InsightStore(this.workAPI)

  jobRatingStore = new JobRatingStore(this.jobRatingAPI)

  termsOfUseStore = new TermsOfUseStore(this.iamAPI)

  individualProfileStore = new IndividualProfileStore(this.individualProfileAPI)

  businessProfileStore = new BusinessProfileStore(this.businessProfileAPI)

  taxRefundMethodStore = new TaxRefundMethodStore(this.taxRefundMethodAPI)

  infoGatheringStore = new InfoGatheringStore(
    this.workAPI.infoGathering,
    this.workRealtime$,
    this.documentStore.primeDocument,
    this.documentTypeStore.primeDocumentTypeByShortId,
  )

  consultationStore = new ConsultationStore(
    this.workAPI,
    this.jobStore.refreshJob,
    this.roomStore.primeRoom,
  )

  opportunitiesAdapter = new OpportunitiesAdapter(
    this.opportunityStore,
    this.consultationStore,
  )

  joinWorkspaceAsAdminAdapter = new JoinWorkspaceAsAdminAdapter(
    this.workspaceStore,
    this.iamAPI,
  )

  opportunityOfferStore = new OpportunityOfferStore(
    this.opsAPI,
    // Jobs Port
    {
      acceptOffer: async (id) => {
        await this.jobStore.pickupInternal(id)
      },
    },
    (o) => this.userStore.primeUser(o) ?? User.none,
    this.workspaceStore.primeWorkspace,
    this.getCurrentMember,
    this.opportunityStore.canViewOpportunities$,
  )

  ocrScopeWorkStore = new OCRScopeWorkStore(this.wunderAPI.ocrScopeWork)

  txfJobStore = new TxfJobStore(this.workAPI)

  payoutsAdminViewModel =
    this.payoutReviewStore && this.adminPayoutHistoryStore
      ? new PayoutsAdminRootViewModel(
          Env,
          this.payoutReviewStore,
          this.adminPayoutHistoryStore,
          new PayoutsAdminModuleAdapter(
            this.newAlert,
            this.getCurrentUser,
            this.downloader,
            this.workspaceStore,
            this.memberStore,
          ),
        )
      : null

  adminViewModel = new AdminViewModel(
    this.newAlert,
    this.inventoryStore,
    this.bundleStore,
    this.scopeChangeRequestStore,
    this.workspaceMembershipStore.fetchWorkspaceMemberships,
    this.workspacePostingStore,
    this.payoutsAdminViewModel,
    this.bookkeepingManagerStore,
    (ns) => new FilterStorage(localStorage, ns),
    this.jobStore.fetchJobs.bind(this.jobStore),
    this.sessionStore.currentMember$,
    this.getCurrentMember,
    new SupportingItemsAdapter(this.documentTypeStore),
    this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships,
    this.jobStore,
    this.iamAPI,
  )

  completeRegistrationViewModel = new CompleteRegistrationViewModel(
    this.iamAPI.users,
    this.router,
  )

  invitationStore = new InvitationStore(this.iamAPI)
  roleStore = new RoleStore(this.iamAPI, this.getCurrentWorkspace)
  skillStore = new SkillStore(this.iamAPI, this.getCurrentWorkspace)

  couponStore = new CouponStore(this.billingAPI)

  billingStore = new BillingStore(
    this.billingAPI,
    this.getCurrentWorkspace,
    this.getCurrentUser,
    this.getCurrentMember,
    this.teamMemberStore.getTeamMember,
    this.workspaceStore.primeWorkspace,
    this.teamMemberStore.fetchUserTeamMembersForWorkspace,
    this.userIdLookup,
    this.sessionStore.currentMember$,
  )

  payoutMethodStore = new PayoutMethodStore(
    this.sessionStore.currentUser$,
    this.bigMoneyPayoutsAPI?.payoutMethods,
    isPayoutsEnabled(),
  )

  adminPayoutMethodStore = new AdminPayoutMethodStore(
    this.getCurrentUser,
    this.bigMoneyPayoutsAPI?.payoutMethods,
  )

  notificationStore = new NotificationStore(
    this.notificationAPI,
    this.memberStore.primeMember,
  )

  notificationPreferencesStore = new NotificationPreferencesStore(
    this.notificationAPI,
  )

  desktopNotificationStore = new DesktopNotificationStore(
    this.sessionStore.currentUser$,
  )

  userDataStore = new UserDataStore(this.iamAPI)

  workspacePostingOpportunityStore = new WorkspacePostingOpportunityStore(
    this.workspacePostingOpsAPI,
    this.workspaceStore.primeWorkspace,
    this.getCurrentUser,
    this.userDataStore.fetchByPublicId,
    this.sessionStore.currentUser$,
  )

  bookkeepingProgressionStore = new BookkeepingProgressionStore(
    this.bookkeepingApi,
  )

  workspaceOpportunitiesAdapter = new WorkspaceOpportunitiesAdapter(
    this.workspacePostingOpportunityStore,
  )

  workspaceOpportunityInvitationStore = new WorkspaceOpportunityInvitationStore(
    this.workspacePostingOpsAPI,
    this.workspacePostingOpportunityStore.canViewOpportunities$,
  )

  featureFlagStore = new FeatureFlagStore(
    this.featureFlagsAPI,
    this.getCurrentUser,
  )

  documentDetailsRootViewModel = new DocumentDetailsRootViewModel(
    new DocumentDetailsAdapter(
      this.getCurrentMember,
      this.documentTypeStore,
      this.documentStore,
      this.documentAccessStore,
      this.documentVersionStore,
      this.jobStore,
      this.newAlert,
    ),
  )

  documentActionsAdapter = new DocumentActionsAdapter(
    this.documentStore,
    this.documentVersionStore,
    this.documentTagStore,
  )

  documentRequestStore = new DocumentRequestStore(this.workAPI)

  /* Controller for all View Models */
  rootController = new ContextController(
    this.newAlert,
    this.notificationPreferencesStore,
    this.memberStore,
    this.jobStore,
    this.teamStore,
    this.teamMemberStore,
    this.teamRoleStore,
    this.documentStore,
    this.documentVersionStore,
    this.documentAccessStore,
    this.documentTagStore,
    this.invitationStore,
    this.roleStore,
    this.skillStore,
    this.billingStore,
    this.workspaceStore,
    this.getCurrentUser,
    this.sessionStore.currentMember$,
    this.router,
    this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships,
    this.documentDetailsRootViewModel.viewDocumentDetails.bind(
      this.documentDetailsRootViewModel,
    ),
    (...args) =>
      this.paymentMethodsRootViewModel.newPaymentMethodModal(...args),
    this.documentActionsAdapter,
    this.payoutMethodStore,
    this.iamProviderStore,
    this.userDataStore,
    () => this.authenticationViewModel.workspaceSelectorState,
  )

  /* View Models */

  cardPaymentMethodEditorAdapter = CardPaymentMethodEditorAdapter(
    this.billingStore,
    Env.STRIPE_PK,
  )

  shortcuts = new Shortcuts(this.keyboard$)

  alertsController = new AlertsController()

  paymentMethodsRootViewModel = new PaymentMethodsRootViewModel(
    this.cardPaymentMethodEditorAdapter,
    PaymentMethodSelectorAdapter(this.billingStore),
  )

  progressionStore = new JobProgressionStore(this.jobProgressionAPI)

  availableJobProgressionFlowsStore = new AvailableJobProgressionFlowsStore(
    this.jobProgressionAPI,
  )

  termsModalViewModel = new TermsModalViewModel(
    this.termsOfUseStore,
    this.sessionStore.currentMember$,
  )

  proSoftwareReviewModalViewModel = new ProSoftwareReviewModalViewModel(
    this.newAlert,
    this.iamProviderStore,
    this.userDataStore,
    this.sessionStore.currentMember$,
  )

  jobCreationRootVM = new JobCreationRootViewModel(
    new JobCreationAdapter(
      this.getCurrentMember,
      this.workspaceMembershipStore,
      this.memberStore,
      this.teamMemberStore,
      this.jobStore,
      this.providerStore,
      this.publishedLegendStore,
      this.questionStore,
      this.summaryStore,
      this.documentStore,
      this.documentVersionStore,
      this.documentAccessStore,
      this.couponStore,
      this.billingStore,
      this.paymentMethodsRootViewModel,
      this.newAlert,
      this.shortcuts,
    ),
  )

  jobDocumentsViewModelAdapter = new JobDocumentsAdapter(
    this.documentStore,
    this.documentTagStore,
    this.documentTypeStore,
    this.documentActionsAdapter,
    this.newAlert,
  )

  jobCreationDialogViewModel = new JobCreationDialogViewModel(
    this.router,
    this.newAlert,
    this.jobCreationRootVM,
  )

  authenticationViewModel = new AuthenticationViewModel(
    this.sessionStore,
    this.workspaceMembershipStore,
    this.invitationStore,
    this.workspaceStore,
    this.memberStore,
    this.authStore,
    this.userDataStore,
    this.router,
    this.newAlert,
    new LastWorkspaceStorage(localStorage),
  )

  documentManualVerificationVM = new DocumentManualVerificationViewModel(
    this.documentActionsAdapter,
    this.newAlert,
  )

  jobRootViewModel = new JobRootViewModel(
    this.router,
    this.newAlert,
    this.jobStore,
    this.consultationStore,
    this.jobTicketStore,
    this.jobInsightStore,
    this.progressionStore,
    this.availableJobProgressionFlowsStore,
    this.desktopNotificationStore,
    this.milestoneStore,
    this.questionStore.createSessionForJob,
    this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships,
    this.teamMemberStore.search,
    this.teamMemberStore.fetchTeamMember,
    this.teamMemberStore.fetchUserTeamMembers,
    this.memberStore.search,
    this.teamMemberStore.find,
    this.providerStore,
    (convo, team, conversationViewModelAdapter) =>
      new ConversationViewModel(
        convo,
        team,
        conversationViewModelAdapter,
        this.getCurrentMember,
      ),
    this.documentStore,
    this.documentVersionStore,
    this.documentTypeStore,
    this.documentAccessStore,
    this.shortcuts,
    this.pageActivity$,
    this.getCurrentMember,
    this.opportunityStore.probeCanViewOpportunities,
    this.documentDetailsRootViewModel.viewDocumentDetails,
    this.paymentMethodsRootViewModel.selectPaymentMethod,
    this.jobDocumentsViewModelAdapter,
    (getWorkspaceId, onReplySelected) =>
      new MessageBankDialogViewModel(
        getWorkspaceId,
        onReplySelected,
        this.messageBankStore,
      ),
    this.sureprepStore,
    this.billingStore,
    this.publishedLegendStore,
    this.userDataStore,
    this.jobSearchStore,
    (id) => this.termsModalViewModel.ensureTermsAccepted(id),
    this.conversationStore,
    this.teamMemberStore.filterTeamMembers.bind(this.teamMemberStore),
    this.couponStore,
    this.inventoryStore,
    this.questionStore,
    this.teamStore,
    this.infoGatheringStore,
    this.jobCreationDialogViewModel,
    this.jobRatingStore,
    this.bookkeepingProgressionStore,
    new SupportingItemsAdapter(this.documentTypeStore),
    this.documentRequestStore,
    this.roomStore,
    this.individualProfileStore,
    this.businessProfileStore,
    this.taxRefundMethodStore,
    this.authenticationViewModel.workspaceSelectorState,
    this.documentManualVerificationVM,
    this.txfJobStore,
    this.platformDelegate.getAddToCalendarDelegate,
    this.platformDelegate.getExternalUrlDelegate,
  )

  stripeCallbackViewModel = new StripeCallbackViewModel(this.getCurrentMember)

  opportunitiesRootViewModel = new OpportunitiesRootViewModel(
    this.opportunitiesAdapter,
    {
      listProOpenJobCounts: this.jobStatsStore.listProOpenJobCounts,
    },
    // IAM Port
    {
      fetchCurrentUserTeamMembers: (workspace) =>
        this.teamMemberStore.fetchUserTeamMembersForWorkspace({
          workspace_id: Id.assert(workspace.id),
          include_team: true,
        }),
      getCurrentUserTeamMembers: (workspace) =>
        this.teamMemberStore.teamMembersForWorkspaceMember(workspace.id),
      getCurrentUserWorkspaceMemberships:
        this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships,
      fetchUserData: this.userDataStore.fetchByPublicId,
    },
    this.getCurrentMember,
    this.newAlert,
    this.router,
    this.jobStore.jobEvents$,
    this.sessionStore.currentMember$,
    this.userDataStore,
    this.workspaceMembershipStore,
    this.opportunityOfferStore,
    this.consultationModalViewModel,
    this.workspaceOpportunitiesAdapter,
    this.workspaceOpportunityInvitationStore,
  )

  jobManagementViewModel = this.makeJobManagementViewModel('JobManagement')

  notificationsSidebarVM = new NotificationsSidebarViewModel(
    this.notificationStore,
    this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships,
    this.sessionStore.currentMember$,
    this.getTime,
  )

  proPayoutsViewModel =
    this.bigMoneyPayoutsAPI && this.proPayoutStore
      ? new ProPayoutsViewModel(
          this.proPayoutStore,
          new ProPayoutsModuleAdapter(
            this.newAlert,
            this.downloader,
            this.getCurrentUser,
            this.userDataStore,
          ),
        )
      : null

  firmDashboardViewModel = new FirmDashboardViewModel(
    new JobSearchAdapter(this.jobSearchStore),
    this.getCurrentMember,
    this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships,
    this.sessionStore.currentMember$,
    localStorage,
    this.userDataStore,
    this.newAlert,
    this.teamMemberStore,
    this.workspaceMembershipStore,
    this.teamStore,
    this.publishedLegendStore,
    this.jobTicketStore,
    this.jobRootViewModel,
    this.jobStatsAPI,
    this.getCurrentUser,
    this.searchServiceItems,
    () => this.documentTypeStore.fetchTypes(),
  )

  ocrScopingJobsViewModel = new OCRScopingJobsViewModel(
    this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships,
    this.ocrScopeWorkStore,
    this.jobStore,
    this.publishedLegendStore,
    this.memberStore,
    this.documentStore,
    this.sessionStore.currentMember$,
    this.getCurrentMember,
    this.teamMemberStore.search,
    this.teamMemberStore.find,
    this.newAlert,
    this.router,
    new JobCreationRootViewModel(
      new JobCreationAdapter(
        this.getCurrentMember,
        this.workspaceMembershipStore,
        this.memberStore,
        this.teamMemberStore,
        this.jobStore,
        this.providerStore,
        this.publishedLegendStore,
        this.questionStore,
        this.summaryStore,
        this.documentStore,
        this.documentVersionStore,
        this.documentAccessStore,
        this.couponStore,
        this.billingStore,
        this.paymentMethodsRootViewModel,
        this.newAlert,
        this.shortcuts,
      ),
    ),
  )

  userManagementViewModel = new UserManagementViewModel(
    this.getCurrentMember,
    localStorage,
    this.memberStore,
    this.roleStore,
    this.searchableUserStore,
    this.skillStore,
    this.teamStore,
    this.teamRoleStore,
    this.userDataStore,
    this.workspaceMembershipStore,
    this.customerStore,
    this.newAlert,
    this.authenticationViewModel.workspaceSelectorState,
    new LastWorkspaceStorage(localStorage, 'userManagement'),
  )

  clientUserManagementViewModel = new ClientUserManagementViewModel(
    this.getCurrentMember,
    localStorage,
    this.searchableUserStore,
    this.workspaceMembershipStore,
    this.memberStore,
    this.customerStore,
    this.newAlert,
  )

  callCenterJobCreationRootViewModel = new JobCreationRootViewModel(
    new JobCreationAdapter(
      this.getCurrentMember,
      this.workspaceMembershipStore,
      this.memberStore,
      this.teamMemberStore,
      this.jobStore,
      this.providerStore,
      this.publishedLegendStore,
      this.questionStore,
      this.summaryStore,
      this.documentStore,
      this.documentVersionStore,
      this.documentAccessStore,
      this.couponStore,
      this.billingStore,
      this.paymentMethodsRootViewModel,
      this.newAlert,
      this.shortcuts,
    ),
  )

  callCenterViewModel = new CallCenterViewModel(
    this.freshbooksAPI.callCenter,
    this.workspaceMembershipStore,
    this.workspaceStore,
    this.iamAPI,
    this.memberStore,
    this.customerStore,
    this.jobSearchStore,
    this.jobStore,
    this.publishedLegendStore,
    this.callCenterJobCreationRootViewModel,
    this.summaryStore,
    this.couponStore,
    this.billingStore,
    this.newAlert,
    this.router,
  )

  userDetailsViewModel = new UserDetailsViewModel(
    this.sessionStore.currentMember$,
    this.getCurrentMember,
    this.billingStore,
    this.adminPayoutMethodStore,
    this.couponStore,
    this.documentStore,
    this.documentTypeStore,
    this.jobStore,
    this.jobSearchStore,
    this.memberStore,
    this.iamProviderStore,
    this.roleStore,
    this.searchableUserStore,
    this.skillStore,
    this.teamStore,
    this.teamMemberStore,
    this.teamRoleStore,
    this.userDataStore,
    this.workspaceMembershipStore,
    this.joinWorkspaceAsAdminAdapter,
    this.newAlert,
    () => this.userManagementViewModel.canAccessUserManagement,
    this.jobCreationDialogViewModel,
    this.userManagementViewModel.manageSkillsListVM,
    this.authStore.logout,
    this.makeJobManagementViewModel,
    this.jobStatsStore,
    this.documentDetailsRootViewModel,
    (ns) => new FilterStorage(localStorage, ns),
    this.customerStore,
  )

  clientUserDetailsViewModel = new ClientUserDetailsViewModel(
    this.sessionStore.currentMember$,
    this.getCurrentMember,
    this.documentStore,
    this.documentTypeStore,
    this.jobStore,
    this.jobSearchStore,
    this.memberStore,
    this.newAlert,
    () => this.clientUserManagementViewModel.canAccessClientUserManagement,
    this.jobCreationDialogViewModel,
    this.makeJobManagementViewModel,
    this.jobStatsStore,
    this.documentDetailsRootViewModel,
    (ns) => new FilterStorage(localStorage, ns),
  )

  /**
   * Checkout view model
   */
  checkoutVM = new CheckoutViewModel(
    this.summaryStore,
    this.jobStore,
    this.publishedLegendStore,
    this.couponStore,
    this.billingStore,
    this.cardPaymentMethodEditorAdapter,
    this.newAlert,
  )

  /**
   * Session tracking feature for PostHog session recording.
   */
  tracking = SessionRecordingFeature(
    this.sessionStore.currentMember$,
    this.authStore.logout$,
    this.userDataStore.fetchByPublicId,
  )

  /**
   * Subscription for observables of composed behavior.
   */
  private subscription = merge(
    LastSeenSync(
      this.sessionStore.currentMember$,
      this.authStore.logout$,
      this.memberStore.updateLastLogin,
      this.memberStore.updateLastSeen,
    ),
    MessageSounds(this.conversationStore.newMessages$).pipe(
      tap((sound) => this.soundPlayer.play(sound)),
    ),
    MessageSystemNotificationsFlow(
      // Any other conversation hosts can merge their streams here.
      // Once we have team conversations, it will come in handy.
      this.jobRootViewModel.messageNotifications$,
      this.workspaceStore.fetch,
      this.desktopNotificationStore.show,
    ),
    // System Notification streams.
    merge(
      NotificationsToSystemNotifications(
        this.notificationStore.newNotifications$,
        this.router.push,
      ),
      this.opportunitiesRootViewModel.opportunitiesViewModel
        .systemNotifications$,
    ).pipe(tap(this.desktopNotificationStore.show)),
  ).subscribe()

  /**
   * Constructor.
   *
   * @param getTime
   * @param keyboard$
   * @param pageActivity$
   */
  constructor(
    private getTime: () => Date,
    private keyboard$: Observable<KeyPress>,
    private pageActivity$: Observable<PageActivity>,
  ) {}

  /**
   * Disposes the store.
   */
  dispose() {
    this.subscription.unsubscribe()
    this.authStore.dispose()
  }
}

function createProxyRouter(rootStore: RootStore): Router {
  return {
    push: (path) => rootStore.routerInstance.push(path),
    replace: (path) => rootStore.routerInstance.replace(path),
    get location() {
      return rootStore.routerInstance.location
    },
  }
}

/**
 * Creates a router instance that throws when called.
 */
function createUnsetRouterInstance(): Router {
  return {
    location: {
      hash: '',
      pathname: '',
      search: '',
      key: '',
    },
    push() {
      throw new Error('Router has not been set.')
    },
    replace() {
      throw new Error('Router has not been set.')
    },
  }
}

/**
 * Whether the Payouts feature is enabled.
 * Used to conditionally construct infrastructure related to payouts.
 */
function isPayoutsEnabled() {
  return Boolean(Env.BIGMONEY_API)
}
